/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TCarouselItem, TCarouselRail } from "../../model/types";

/**
 * Shared carousel pagination types and helpers.
 * Used by any screen that renders cursor-paginated carousel rails
 * or offset-paginated tiles — HomeScreen, PlayerCarousel, etc.
 *
 * Framework-free by design: no React, no Redux, no DOM.
 */

// ─── Types ───

/** State for cursor-paginated carousel rails. */
export interface TCarouselRailPaginationState {
  items: TCarouselRail[];
  pageCursor: string | null;
  isLoading: boolean;
  error: string;
  /**
   * Catalog error code for the screen's error dialog, composed by the screen's
   * own error mapper — e.g. `"A707-G500"`, or `"A707"` when there is no
   * sub-error code to show. Empty string = no dialog.
   *
   * Optional because it is newer than the persisted state shape: a session
   * restored from a build that predates it arrives without the field. Readers
   * must treat `undefined` as "no error", which is what
   * `selectHomeScreenErrorCode` does.
   */
  errorCode?: string;
}

/** Shape the carousel-rail mapper produces per page. */
export interface TCarouselRailPage {
  items: TCarouselRail[];
  pageCursor: string | null;
}

/** State for offset-paginated tiles within a single carousel. */
export interface TTilePaginationState {
  /** Accumulated tiles from every page loaded so far. */
  items: TCarouselItem[];
  /** Total items available on the server. null until the first response. */
  resolvedCount: number | null;
  isLoading: boolean;
  error: string;
}

/** Shape the tile mapper produces per page. */
export interface TTilePage {
  items: TCarouselItem[];
  /** Total items available on the server, as reported by the response. */
  resolvedCount: number;
}

// ─── Tile Fetch Constants ───

/** Tiles requested per carousel fetch — shared by all screens. */
export const CAROUSEL_TILE_PAGE_SIZE: number = 10;

/**
 * Max tiles a rail loads/renders in-line before the trailing See More tile
 * takes over the tail. A rail whose true collection (resolvedCount) exceeds
 * this cap shows exactly this many tiles + a See More tile at the next slot.
 *
 * This is the single source of truth for the cap — the browser render
 * (ContentCarousels), the D-pad bound (HomeScreenController), and the tile
 * fetch guard below all key off it, so the See More slot has one fixed index
 * that pagination can never shift.
 */
export const SEE_MORE_RAIL_TILE_CAP: number = 15;

// ─── Guard Functions ───

/** Returns true when a cursor-paginated carousel rail fetch should be issued. */
export function canLoadMoreCursor(
  state: TCarouselRailPaginationState
): boolean {
  const hasMore =
    (state.pageCursor !== null && state.pageCursor !== "") ||
    state.items.length === 0;
  return hasMore && !state.isLoading;
}

/**
 * Returns true when an offset-paginated tile fetch should be issued.
 *
 * When `isSeeMoreNeeded` is true, paging stops once the rail has
 * SEE_MORE_RAIL_TILE_CAP tiles even if the server has more
 * (resolvedCount > cap) — those extra tiles are reachable only through the See
 * More grid, so streaming them into the rail would just render over the See
 * More slot. When false (the default) it behaves as before: page the whole
 * rail in-line up to resolvedCount.
 */
export function canLoadMoreOffset(
  state: TTilePaginationState,
  isSeeMoreNeeded: boolean = false
): boolean {
  // See-More rails are hard-stopped at the cap regardless of resolvedCount.
  if (isSeeMoreNeeded && state.items.length >= SEE_MORE_RAIL_TILE_CAP) {
    return false;
  }
  const hasMore =
    state.resolvedCount === null || state.items.length < state.resolvedCount;
  return hasMore && !state.isLoading;
}

/**
 * Tiles to request on the next fetch.
 *
 * Normally CAROUSEL_TILE_PAGE_SIZE. When `isSeeMoreNeeded` is true the LAST
 * fetch is clamped so the accumulated total lands exactly on the cap instead
 * of overshooting — this is the "initial x → next (cap - x)" rule. E.g. a rail
 * at 10 loaded requests 5, not another full 10 (which would push the See More
 * slot to index 20 instead of the fixed cap index).
 *
 * Returns 0 when a See-More rail is already at/over the cap — callers treat
 * that as "don't fetch" (canLoadMoreOffset will already have returned false).
 */
export function nextFetchCount(
  loaded: number,
  isSeeMoreNeeded: boolean = false,
  pageSize: number = CAROUSEL_TILE_PAGE_SIZE
): number {
  if (!isSeeMoreNeeded) {
    return pageSize;
  }
  const remaining: number = SEE_MORE_RAIL_TILE_CAP - loaded;
  if (remaining <= 0) {
    return 0;
  }
  // "next = cap - x" — clamp the tail fetch, full page otherwise.
  return remaining < pageSize ? remaining : pageSize;
}

// ─── Reducer Helpers ───

/**
 * Apply a fulfilled carousel-rail page by appending items.
 * Mutates draft state directly — safe inside Redux Toolkit reducers.
 */
export function applyCursorPage(
  state: TCarouselRailPaginationState,
  page: TCarouselRailPage
): void {
  state.items = state.items.concat(page.items);
  state.pageCursor = page.pageCursor;
  state.isLoading = false;
  state.error = "";
}

/**
 * Apply a fulfilled tile page by appending items.
 * Mutates draft state directly — safe inside Redux Toolkit reducers.
 */
export function applyOffsetPage(
  state: TTilePaginationState,
  page: TTilePage
): void {
  state.items = state.items.concat(page.items);
  state.resolvedCount = page.resolvedCount;
  state.isLoading = false;
  state.error = "";
}
